import React, { useEffect } from 'react';
import { View, Text } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withDelay,
} from 'react-native-reanimated';
import { ArrowRight } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';

export interface JourneyStep {
  label: string;
  count: number;
  percentage: number;
  isHappyPath: boolean;
}

interface JourneyFlowProps {
  steps: JourneyStep[];
  totalStart: number;
}

export function JourneyFlow({ steps, totalStart }: JourneyFlowProps) {
  const colors = useThemeColors();

  if (!steps.length) return null;

  return (
    <View className="gap-0">
      {steps.map((step, idx) => {
        const dropOff = idx > 0
          ? steps[idx - 1].count - step.count
          : 0;
        const dropOffPct = idx > 0 && steps[idx - 1].count > 0
          ? Math.round((dropOff / steps[idx - 1].count) * 100)
          : 0;

        return (
          <View key={`step-${idx}`}>
            {/* Drop-off indicator between steps */}
            {idx > 0 && (
              <DropOffConnector
                dropOff={dropOff}
                dropOffPct={dropOffPct}
                index={idx}
                colors={colors}
              />
            )}

            {/* Step box */}
            <StepBox
              step={step}
              index={idx}
              isLast={idx === steps.length - 1}
              colors={colors}
            />
          </View>
        );
      })}
    </View>
  );
}

function StepBox({
  step,
  index,
  isLast,
  colors,
}: {
  step: JourneyStep;
  index: number;
  isLast: boolean;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const widthAnim = useSharedValue(0);

  useEffect(() => {
    widthAnim.value = withDelay(
      index * 120,
      withTiming(step.percentage, { duration: 600 })
    );
  }, [step.percentage]);

  const barStyle = useAnimatedStyle(() => ({
    width: `${widthAnim.value}%`,
  }));

  const isGood = step.percentage >= 50;
  const borderColor = step.isHappyPath
    ? isGood ? '#22C55E' : '#F59E0B'
    : colors.border.light;

  return (
    <View
      className="rounded-xl overflow-hidden"
      style={{
        borderWidth: 1.5,
        borderColor,
        backgroundColor: colors.surface.primary,
      }}
    >
      {/* Progress bar background */}
      <Animated.View
        style={[
          barStyle,
          {
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            backgroundColor: step.isHappyPath
              ? isGood ? 'rgba(34, 197, 94, 0.06)' : 'rgba(245, 158, 11, 0.06)'
              : `${colors.primary[600]}06`,
          },
        ]}
      />

      {/* Content */}
      <View className="flex-row items-center justify-between px-4 py-3">
        <View className="flex-row items-center gap-3 flex-1">
          {/* Step number */}
          <View
            className="w-6 h-6 rounded-full items-center justify-center"
            style={{
              backgroundColor: step.isHappyPath
                ? isGood ? 'rgba(34, 197, 94, 0.15)' : 'rgba(245, 158, 11, 0.15)'
                : colors.surface.secondary,
            }}
          >
            <Text
              className="text-[10px] font-bold"
              style={{
                color: step.isHappyPath
                  ? isGood ? '#16A34A' : '#D97706'
                  : colors.text.tertiary,
              }}
            >
              {index + 1}
            </Text>
          </View>

          <Text
            style={{ color: colors.text.primary }}
            className="text-sm font-medium flex-1"
            numberOfLines={1}
          >
            {step.label}
          </Text>
        </View>

        <View className="flex-row items-center gap-3">
          <Text style={{ color: colors.text.primary }} className="text-sm font-bold">
            {step.count}
          </Text>
          <View
            className="px-2 py-0.5 rounded-md"
            style={{
              backgroundColor: step.percentage >= 50
                ? 'rgba(34, 197, 94, 0.1)'
                : step.percentage >= 25
                ? 'rgba(245, 158, 11, 0.1)'
                : 'rgba(239, 68, 68, 0.1)',
            }}
          >
            <Text
              className="text-[10px] font-bold"
              style={{
                color: step.percentage >= 50
                  ? '#16A34A'
                  : step.percentage >= 25
                  ? '#D97706'
                  : '#DC2626',
              }}
            >
              {step.percentage.toFixed(1)}%
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
}

function DropOffConnector({
  dropOff,
  dropOffPct,
  index,
  colors,
}: {
  dropOff: number;
  dropOffPct: number;
  index: number;
  colors: ReturnType<typeof useThemeColors>;
}) {
  return (
    <View className="flex-row items-center py-1 pl-6">
      {/* Vertical connector line */}
      <View
        className="w-0.5 h-3 mr-2"
        style={{ backgroundColor: colors.border.light }}
      />

      {/* Arrow */}
      <ArrowRight size={10} color={colors.text.tertiary} style={{ marginRight: 4 }} />

      {/* Drop-off label */}
      {dropOff > 0 && (
        <Text className="text-[10px] font-medium" style={{ color: '#EF4444' }}>
          -{dropOff} dropped ({dropOffPct}%)
        </Text>
      )}
    </View>
  );
}
